import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert
} from '@mui/material';
import dayjs from 'dayjs';

interface MappingFormDialogProps {
  open: boolean;
  loading: boolean;
  mappingForm: {
    sessionDate: string;
    notes: string;
  };
  onClose: () => void;
  onFormChange: (form: { sessionDate: string; notes: string }) => void;
  onSubmit: () => void;
}

interface FollowupFormDialogProps {
  open: boolean;
  loading: boolean;
  baselineSessions: any[];
  selectedBaselineForFollowup: string;
  followupForm: {
    sessionDate: string;
    notes: string;
    intervalMonths: number;
  };
  onClose: () => void;
  onBaselineChange: (baselineId: string) => void;
  onFormChange: (form: { sessionDate: string; notes: string; intervalMonths: number }) => void;
  onSubmit: () => void;
}

export const MappingFormDialog: React.FC<MappingFormDialogProps> = ({
  open,
  loading,
  mappingForm,
  onClose,
  onFormChange,
  onSubmit
}) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Create New Mole Mapping Session</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, width: 400, maxWidth: '100%' }}>
          <TextField
            label="Session Date"
            type="date"
            fullWidth
            margin="normal"
            value={mappingForm.sessionDate}
            onChange={(e) => onFormChange({
              ...mappingForm,
              sessionDate: e.target.value
            })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Notes"
            fullWidth
            margin="normal"
            multiline
            rows={3}
            value={mappingForm.notes}
            onChange={(e) => onFormChange({
              ...mappingForm,
              notes: e.target.value
            })}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={onSubmit}
          disabled={loading || !mappingForm.sessionDate}
        >
          {loading ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const FollowupFormDialog: React.FC<FollowupFormDialogProps> = ({
  open,
  loading,
  baselineSessions,
  selectedBaselineForFollowup,
  followupForm,
  onClose,
  onBaselineChange,
  onFormChange,
  onSubmit
}) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Create Follow-up Session</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, width: 400, maxWidth: '100%' }}>
          {baselineSessions.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No baseline sessions found. Please create a baseline session first.
            </Alert>
          ) : (
            <FormControl fullWidth margin="normal">
              <InputLabel>Baseline Session</InputLabel>
              <Select
                value={selectedBaselineForFollowup}
                onChange={(e) => onBaselineChange(e.target.value)}
                label="Baseline Session"
              >
                {baselineSessions.map((session) => (
                  <MenuItem key={session.id} value={session.id}>
                    {dayjs(session.sessionDate).format('MMMM D, YYYY')}
                    {session.notes && ` - ${session.notes.substring(0, 30)}...`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          <TextField
            label="Session Date"
            type="date"
            fullWidth
            margin="normal"
            value={followupForm.sessionDate}
            onChange={(e) => onFormChange({
              ...followupForm,
              sessionDate: e.target.value
            })}
            InputLabelProps={{ shrink: true }}
          />
          
          <TextField
            label="Interval (Months)"
            type="number"
            fullWidth
            margin="normal"
            value={followupForm.intervalMonths}
            onChange={(e) => onFormChange({
              ...followupForm,
              intervalMonths: parseInt(e.target.value) || 0
            })}
          />
          
          <TextField
            label="Notes"
            fullWidth
            margin="normal"
            multiline
            rows={3}
            value={followupForm.notes}
            onChange={(e) => onFormChange({
              ...followupForm,
              notes: e.target.value
            })}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={onSubmit}
          disabled={loading || !selectedBaselineForFollowup || !followupForm.sessionDate || baselineSessions.length === 0}
        >
          {loading ? 'Creating...' : 'Create Follow-up'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
